import React, { useState } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  ExportProduct,
  ExportClassificationRegime,
  ExportClassificationResult,
} from 'api/ExportHubAPI';

interface Props {
  product: ExportProduct;
  classifications: ExportClassificationResult[];
  /** Owning database, for the workflow link's push target. */
  databaseId: string;
}

/**
 * The four export-control findings this product carries, as produced by an
 * Export Workflow run. This panel only *displays* — nothing is computed here.
 * Results arrive from the workflow's own "Push to Export Hub" action.
 *
 * The workflow produces more than these four (Product Profile, Schedule B,
 * Sensitive Tech); only the regimes that establish the controlling export
 * regime are surfaced here.
 */

const REGIMES: Array<{
  key: ExportClassificationRegime;
  name: string;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  /** Which regime this finding supports as jurisdiction evidence (spec §4.6). */
  feeds?: string;
}> = [
  { key: 'country', name: 'Country Screening', icon: PublicOutlinedIcon },
  { key: 'nrc', name: 'NRC Check', icon: ScienceOutlinedIcon },
  { key: 'itar', name: 'ITAR Check', icon: GavelOutlinedIcon, feeds: 'ITAR' },
  {
    key: 'ear_ccl',
    name: 'EAR / CCL Check',
    icon: VerifiedUserOutlinedIcon,
    feeds: 'EAR',
  },
];

const STATUS_VARIANT: Record<string, string> = {
  cleared: 'success',
  license_required: 'warning',
  flagged: 'danger',
  needs_review: 'info',
  no_data: 'muted',
  error: 'danger',
};

const STATUS_LABEL: Record<string, string> = {
  cleared: 'Cleared',
  license_required: 'License Required',
  flagged: 'Flagged',
  needs_review: 'Needs Review',
  no_data: 'No Data',
  error: 'Error',
};

const CONTROLLING = ['flagged', 'needs_review', 'license_required'];

/**
 * Whether this status earns a pill at all.
 *
 * 'cleared' deliberately gets none. Each finding is scoped to ONE regime, so an
 * EAR/CCL or NRC check reporting "cleared" only means that regime found nothing
 * — it says nothing about the others, and a product cleared on the CCL may
 * still be ITAR-controlled. A green "Cleared" badge next to a single regime
 * reads as a clearance of the product, which is a claim no one check can make.
 * The summary line underneath still states what the check actually found
 * ("Not subject to 10 CFR Part 110"), which is the honest version.
 */
const showsPill = (status: string): boolean => status !== 'cleared';

/**
 * A finding "disagrees" when it points at a regime the product isn't recorded
 * under — an ITAR hit on a product filed as EAR, say. Flagged on the card
 * itself rather than restated in a separate evidence list.
 */
const disagreesWithRegime = (
  feeds: string | undefined,
  status: string,
  jurisdiction: string,
): boolean =>
  !!feeds && CONTROLLING.includes(status) && feeds !== jurisdiction;

const asStringList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

const asString = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null;

interface CountryLine {
  country: string;
  license_required?: boolean;
  determinable?: boolean;
}
const asCountryLines = (v: unknown): CountryLine[] =>
  Array.isArray(v) ? (v as CountryLine[]) : [];

/** The one-line finding shown on the collapsed card. */
const summarize = (
  regime: ExportClassificationRegime,
  result: ExportClassificationResult,
): string => {
  const r = result.result || {};
  if (result.status === 'no_data' && asString(r.note))
    return asString(r.note) as string;
  switch (regime) {
    case 'country': {
      const lines = asCountryLines(r.license_results);
      const need = lines.filter((l) => l.license_required);
      if (need.length)
        return `License required for ${need.map((l) => l.country).join(', ')}`;
      if (lines.length) return 'No license required for listed destinations';
      return STATUS_LABEL[result.status] || 'Country chart evaluated';
    }
    case 'nrc':
      return CONTROLLING.includes(result.status)
        ? 'Nuclear control indicators identified'
        : 'Not subject to 10 CFR Part 110';
    case 'itar': {
      const cat = asString(r.usml_category);
      if (cat) return `USML Category ${cat} identified`;
      return CONTROLLING.includes(result.status)
        ? 'Potential USML control — review required'
        : 'Not USML-controlled';
    }
    case 'ear_ccl': {
      const conf = asString(r.confidence);
      if (result.eccn)
        return `ECCN ${result.eccn}${conf ? ` (${conf} confidence)` : ''}`;
      return CONTROLLING.includes(result.status)
        ? 'CCL control identified'
        : 'No CCL control identified';
    }
    default:
      return STATUS_LABEL[result.status] || result.status;
  }
};

/** Everything the workflow recorded for one regime. */
const RegimeDetail: React.FC<{
  result: ExportClassificationResult;
  feeds?: string;
}> = ({ result, feeds }) => {
  const r = result.result || {};
  const reasons = asStringList(r.reasons_for_control);
  const countries = asCountryLines(r.license_results);
  const note = asString(r.note);
  const usml = asString(r.usml_category);
  const alternatives = asStringList(r.alternatives);
  // CCL records its missing info under `missing_technical_details`; NRC/ITAR use
  // `missing_information`. Fall back so "Missing information" shows for every regime.
  const missing = asStringList(
    r.missing_information ?? r.missing_technical_details,
  );
  const assumptions = asStringList(r.assumptions);

  return (
    <div className='eh-check-body'>
      <div className='eh-fieldgrid'>
        <div className='eh-field-cell'>
          <div className='eh-field-cell-label'>Result</div>
          <div className='eh-field-cell-value'>
            {showsPill(result.status) ? (
              <span
                className={`eh-pill eh-pill-${
                  STATUS_VARIANT[result.status] || 'muted'
                }`}
              >
                {STATUS_LABEL[result.status] || result.status}
              </span>
            ) : (
              // Em-dash rather than an empty cell: the row is labelled
              // "Result", so blank would read as a rendering failure.
              <span className='eh-field-cell-empty'>—</span>
            )}
          </div>
        </div>
        {!!result.eccn && (
          <div className='eh-field-cell'>
            <div className='eh-field-cell-label'>ECCN</div>
            <div className='eh-field-cell-value eh-mono'>{result.eccn}</div>
          </div>
        )}
        {!!usml && (
          <div className='eh-field-cell'>
            <div className='eh-field-cell-label'>USML Category</div>
            <div className='eh-field-cell-value eh-mono'>{usml}</div>
          </div>
        )}
      </div>

      {!!note && (
        <div className='eh-check-assess'>
          <div className='eh-field-cell-label'>Assessment</div>
          <p className='eh-check-assess-text'>{note}</p>
        </div>
      )}

      {reasons.length > 0 && (
        <div>
          <div className='eh-field-cell-label'>Reasons for control</div>
          <div className='eh-chip-row'>
            {reasons.map((code) => (
              <span className='eh-pill eh-pill-info' key={code}>
                {code}
              </span>
            ))}
          </div>
        </div>
      )}

      {alternatives.length > 0 && (
        <div>
          <div className='eh-field-cell-label'>Alternatives considered</div>
          <div className='eh-chip-row'>
            {alternatives.map((a) => (
              <span className='eh-pill eh-pill-muted eh-mono' key={a}>
                {a}
              </span>
            ))}
          </div>
        </div>
      )}

      {countries.length > 0 && (
        <div>
          <div className='eh-field-cell-label'>Destination licensing</div>
          <div className='eh-check-countries'>
            {countries.map((c) => (
              <div className='eh-check-country static' key={c.country}>
                <span className='eh-check-country-name'>{c.country}</span>
                <span className='eh-check-country-right'>
                  <span
                    className={`eh-pill eh-pill-${
                      c.license_required ? 'warning' : 'success'
                    }`}
                  >
                    {c.license_required
                      ? 'License required'
                      : 'No license required'}
                    {c.determinable === false ? ' (review)' : ''}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* The workflow reports what it could not establish — that gap is the
          reason a finding reads Needs Review, so it belongs on the card. */}
      {missing.length > 0 && (
        <div>
          <div className='eh-field-cell-label'>Missing information</div>
          <ul className='eh-wfr-list'>
            {missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      {assumptions.length > 0 && (
        <div>
          <div className='eh-field-cell-label'>Assumptions</div>
          <ul className='eh-wfr-list'>
            {assumptions.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}

      {!!feeds && CONTROLLING.includes(result.status) && (
        <div className='eh-check-feeds'>
          Supports the recorded regime as {feeds} evidence.
        </div>
      )}
    </div>
  );
};

const ExportWorkflowResults: React.FC<Props> = ({
  product,
  classifications,
  databaseId,
}) => {
  const history = useHistory();
  const [expanded, setExpanded] = useState<
    Partial<Record<ExportClassificationRegime, boolean>>
  >({});

  // Latest result per regime — the API returns them newest-first.
  const latestByRegime: Partial<
    Record<ExportClassificationRegime, ExportClassificationResult>
  > = {};
  classifications.forEach((c) => {
    if (!latestByRegime[c.regime]) latestByRegime[c.regime] = c;
  });

  const present = REGIMES.filter((r) => latestByRegime[r.key]);

  // Provenance: the saved run these findings came from, stamped at ingest.
  const withSource = classifications.find(
    (c) => !!(c.result || {})._source_case_name || !!(c.result || {})._source_case_id,
  );
  const sourceName = withSource
    ? asString((withSource.result || {})._source_case_name)
    : null;
  const sourceAt = present.length
    ? present
        .map((r) => latestByRegime[r.key] as ExportClassificationResult)
        .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at))[0]
        .created_at
    : null;

  /**
   * Open the Export Workflow carrying this product, so its "Push to Export Hub"
   * dialog comes back pre-aimed here. Without the product id a SKU-less product
   * has nothing to match on and the push would create a duplicate.
   */
  const openWorkflow = (): void => {
    const params = new URLSearchParams({
      productId: product.id,
      name: product.name,
      db: product.database_id || databaseId,
    });
    if (product.internal_sku) params.set('sku', product.internal_sku);
    history.push(`/export-page?${params.toString()}`);
  };

  const actions = (
    <div className='eh-panel-head-actions'>
      <button type='button' className='eh-mini-btn' onClick={openWorkflow}>
        <OpenInNewIcon style={{ fontSize: 15 }} /> Open Export Workflow
      </button>
    </div>
  );

  if (present.length === 0) {
    return (
      <>
        <div className='eh-regime-subhead'>
          <span>Workflow findings</span>
          {actions}
        </div>
        <div className='eh-wfr-empty'>
          <AccountTreeOutlinedIcon
            style={{ fontSize: 26, color: 'var(--text-tertiary)' }}
          />
          <div className='eh-wfr-empty-text'>
            <div className='eh-wfr-empty-title'>No workflow results yet</div>
            <p className='eh-wfr-empty-body'>
              Country, NRC, ITAR and EAR/CCL findings come from an Export
              Workflow run. Open the workflow, describe{' '}
              <strong>{product.name}</strong> there
              {product.internal_sku ? (
                <>
                  {' '}
                  using SKU{' '}
                  <span className='eh-mono'>{product.internal_sku}</span>
                </>
              ) : null}
              , then use <strong>Push to Export Hub</strong> — it comes back
              pre-aimed at this product.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className='eh-regime-subhead'>
        <span>
          Workflow findings{' '}
          <span className='eh-count-soft'>
            ({present.length} of {REGIMES.length})
          </span>
        </span>
        {actions}
      </div>

      <div className='eh-wfr-source'>
        <AutoAwesomeIcon style={{ fontSize: 14 }} />
        {sourceName ? (
          <span>
            From <strong>{sourceName}</strong>
            {sourceAt
              ? ` · imported ${moment(sourceAt).format('MMM D, YYYY h:mm A')}`
              : ''}
          </span>
        ) : (
          <span>
            {sourceAt
              ? `Recorded ${moment(sourceAt).format('MMM D, YYYY h:mm A')}`
              : 'Recorded findings'}
          </span>
        )}
      </div>

      {/* Two-up: a collapsed finding is a couple of lines, so a full-width row
          per regime wastes the page. An expanded card spans both columns
          because its detail (destination licensing) needs the width. */}
      <div className='eh-wfr-grid'>
        {REGIMES.map((r) => {
          const Icon = r.icon;
          const latest = latestByRegime[r.key];
          const open = !!expanded[r.key];
          if (!latest) {
            return (
              <div className='eh-wfr-card missing' key={r.key}>
                <span className='eh-check-icon'>
                  <Icon style={{ fontSize: 18 }} />
                </span>
                <span className='eh-check-text'>
                  <span className='eh-check-titleline'>
                    <span className='eh-check-name'>{r.name}</span>
                    <span className='eh-pill eh-pill-muted'>Not in run</span>
                  </span>
                  <span className='eh-check-summary'>
                    This run produced no finding for {r.name}.
                  </span>
                </span>
              </div>
            );
          }
          const conflict = disagreesWithRegime(
            r.feeds,
            latest.status,
            product.jurisdiction,
          );
          return (
            <div
              className={`eh-wfr-card ${open ? 'open' : ''} ${
                conflict ? 'conflict' : ''
              }`}
              key={r.key}
            >
              <button
                type='button'
                className='eh-wfr-toggle'
                onClick={() =>
                  setExpanded((p) => ({ ...p, [r.key]: !p[r.key] }))
                }
                aria-expanded={open}
              >
                <span className='eh-check-icon'>
                  <Icon style={{ fontSize: 18 }} />
                </span>
                <span className='eh-check-text'>
                  <span className='eh-check-titleline'>
                    <span className='eh-check-name'>{r.name}</span>
                    {showsPill(latest.status) && (
                      <span
                        className={`eh-pill eh-pill-${
                          STATUS_VARIANT[latest.status] || 'muted'
                        }`}
                      >
                        {STATUS_LABEL[latest.status] || latest.status}
                      </span>
                    )}
                  </span>
                  <span className='eh-check-summary'>
                    {summarize(r.key, latest)}
                  </span>
                  {conflict && (
                    <span className='eh-wfr-conflict'>
                      <WarningAmberIcon style={{ fontSize: 12 }} />
                      Points to {r.feeds} — recorded as {product.jurisdiction}
                    </span>
                  )}
                </span>
                <ExpandMoreIcon
                  style={{
                    fontSize: 18,
                    flexShrink: 0,
                    color: 'var(--text-tertiary)',
                    transform: open ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s',
                  }}
                />
              </button>
              {open && (
                <div className='eh-check-expanded'>
                  <RegimeDetail result={latest} feeds={r.feeds} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ExportWorkflowResults;
